import {CGP_EVENTS_NAMES, CGPEventSubscription, ICgpSdk} from './cgp-sdk.interface';
import { LiveTables } from '../live-table-feeds-service/live-table-feeds.service.interface';
import {SYSTEM_EVENTS} from "../../../sdk/src/events/events";
import {GNWP_SOURCES} from "../gnwp/gnwp-consts";

export interface OnShowGamePopupCallbackParams {
  gameType?: number,
  gamePopupApprovedCallback: () => void
}

export interface OnGameOpenParams {
  gameType: number;
  isReal: boolean;
}

export interface OnGameCloseParams {
  gameType: number;
  isReal: boolean;
  isGamePlayed: boolean;
  showNextGameSuggestion: boolean;
}

export interface OnPlayerActionParams {
  actionName: string;
  options?: any;
}

export interface OnOauthStateChangedParams {
  loginType?: number;
  logoutReason?: number;
  kickReason?: number;
  fromRegistration?: boolean;
}

export interface IPeriodicGeoUpdateEventParams {
  mobileNumber: string;
  licenseKey: string;
  licenseWebKey: string;
  locationTransactionId: string;
}

export class SdkEvents {
  constructor(private CGP: ICgpSdk) {

  }

  onOAuthStateChanged(callback: (params: OnOauthStateChangedParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.OAUTH_STATE_CHANGE,
      callback: callback,
      context: this
    });
  }

  onBeforeOAuthStateChanged(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.ON_BEFORE_OAUTH_STATE_CHANGE,
      callback: callback,
      context: this
    });
  }

  onDeepLinkReceived(callback: (deepLinkObject: any) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.DEEPLINK_RECEIVED,
      callback: callback,
      context: this
    });
  }

  onShowGamePopup(callback: (args: OnShowGamePopupCallbackParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.GAME_POPUP_SHOW,
      callback: callback,
      context: this
    });
  }

  onGameOpen(callback: (params: OnGameOpenParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.GAME_OPEN,
      callback: callback,
      context: this
    });
  }

  onGameClose(callback: (params?: OnGameCloseParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.CLOSE_GAME,
      callback: callback,
      context: this
    });
  }


  onPlayerAction(callback: (eventData: OnPlayerActionParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.PLAYER_ACTION,
      callback: (data) => callback(data || {} as OnPlayerActionParams),
      context: this
    });
  }


  onLoginFormOpen(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LOGIN_FORM_OPEN,
      callback: callback,
      context: this
    });
  }

  onLoginFormClosed(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LOGIN_FORM_CLOSE,
      callback: callback,
      context: this
    });
  }

  onTokenLifetimeExpired(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.TOKEN_LIFETIME_EXPIRED,
      callback: callback,
      context: this
    });
  }

  onLiveTableFeedsUpdate(callback: (tablesData: LiveTables) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LIVE_TABLES_UPDATE,
      callback: callback,
      context: this
    });
  }

  onRegistrationCompleted(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.REGISTRATION_COMPLETED,
      callback,
      context: this
    });
  }

  onBeginLoginFromRegistration(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.BEGIN_LOGIN_AFTER_REGISTRATION,
      callback,
      context: this
    });
  }

  onLoginCompletedAfterRegistration(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LOGIN_COMPLETED_AFTER_REGISTRATION,
      callback,
      context: this
    });
  }

  onLoginFailedAfterRegistration(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LOGIN_FAILED_AFTER_REGISTRATION,
      callback,
      context: this
    });
  }

  onAppResumed(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.APP_RESUMED,
      callback: callback,
      context: this
    });
  }

  onAppPaused(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.APP_PAUSED,
      callback: callback,
      context: this
    });
  }

  onExternalLinkClosed(callback: (linkSource: string) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.EXTERNAL_LINK_CLOSED,
      callback: callback,
      context: this
    });
  }

  onDepositWidgetClosed(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.DEPOSIT_WIDGET_CLOSED,
      callback: callback,
      context: this
    });
  }

  onCashierOrDepositWidgetClosed(callback: () => void): CGPEventSubscription {
    const subscriptions: CGPEventSubscription[] = [];
    subscriptions.push(this.onDepositWidgetClosed(() => {
      callback();
    }))

    subscriptions.push(this.onExternalLinkClosed((linkSource) => {
      if (GNWP_SOURCES.CASHIER === linkSource) {
        callback();
      }
    }));

    return {
      off: () => {
        subscriptions.forEach(s => s.off);
      }
    };
  }

  onTouchIDActiveStateChanged(callback: (state: boolean) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.TOUCH_ID_STATE_CHANGED,
      callback: callback,
      context: this
    });
  }

  onOmgDisplayed(callback: (state: unknown) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.OMG_DISPLAYED,
      callback,
      context: this
    });
  }

  onLiveGameSwitched(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LIVE_GAME_SWITCHED,
      callback: callback,
      context: this
    });
  }

  onFreeToPlayWidgetClosed(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.FREE_TO_PLAY_WIDGET_CLOSED,
      callback: callback,
      context: this
    });
  }

  onPostMessage(callback: (message: MessageEvent) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.POST_MESSAGE_ARRIVE,
      callback: callback,
      context: this
    });
  }

  onHybridPostMessage(callback: (message: MessageEvent) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.HYBRID_POST_MESSAGE_ARRIVE,
      callback: callback,
      context: this
    });
  }

  onHybridEmbeddedGameLoadingIndicatorUpdateV1(callback: (percentage: number | string) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.LOADING_INDICATOR_EMBEDDED_UPDATE,
      callback: callback,
      context: this
    });
  }

  onHybridEmbeddedGameLoadingIndicatorUpdateV2(callback: (eventData: {statusBar: number | string; numberOfPackages: string}) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.LOADING_INDICATOR_EMBEDDED_UPDATE_V2,
      callback: callback,
      context: this
    });
  }

  onLoginSessionResumed(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.LOGIN_SESSION_RESUMED,
      callback: callback,
      context: this
    });
  }

  onUserApprovedPostLoginPopup(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.USER_APPROVE_GAME_POPUP,
      callback: callback,
      context: this
    });
  }

  onSecondWebViewClose(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.SECOND_WEB_VIEW_CLOSE,
      callback: callback,
      context: this
    });
  }


  onHybridGeoLocationResponse(callback: (result: string) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.GEO_HYBRID_GET_LOCATION_RES,
      callback: callback,
      context: this
    });
  }


  onServerTimeUpdated(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.TIME_UPDATED_FROM_SERVER,
      callback: callback,
      context: this
    });
  }

  onHybridInfoComplete(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.ADD_TAG_OPEN_APP_VER,
      callback: callback,
      context: this
    });
  }
}
